import {
  Instagram, Twitch, Youtube, Twitter, Gamepad2, Music, Video, Globe,
  Facebook, Linkedin, DollarSign, AtSign
} from 'lucide-react';
import type React from 'react';
import { SvgIcon, type SvgIconProps } from '@mui/material';
import { toExternalUrl } from './externalUrl';

// Define a union type for known platform keys
export type SocialPlatformKey =
  | 'instagram' | 'spotify' | 'twitch' | 'youtube' | 'discord' | 'tiktok' | 'twitter'
  | 'facebook' | 'threads' | 'bluesky' | 'linkedin'
  | 'bandcamp' | 'soundcloud' | 'applemusic'
  | 'venmo' | 'cashapp'
  | 'website';

// Platform colors
export const PLATFORM_COLORS: Record<SocialPlatformKey, string> = {
  instagram: '#E1306C',
  spotify: '#1DB954',
  twitch: '#6441A4',
  youtube: '#FF0000',
  discord: '#5865F2',
  tiktok: '#000000',
  twitter: '#1DA1F2',
  facebook: '#1877F2',
  threads: '#000000',
  bluesky: '#0085FF',
  linkedin: '#0A66C2',
  bandcamp: '#1DA0C3',
  soundcloud: '#FF5500',
  applemusic: '#FA243C',
  venmo: '#008CFF',
  cashapp: '#00D632',
  website: '#757575',
};

// Custom Spotify logo as SVG icon
export const SpotifyIcon = (props: SvgIconProps): React.ReactElement => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"
      fill="currentColor"
    />
  </SvgIcon>
);

// Platform icons mapping
export const SOCIAL_PLATFORM_ICONS: Record<SocialPlatformKey, React.ElementType> = {
  instagram: Instagram,
  spotify: SpotifyIcon as any,
  twitch: Twitch,
  youtube: Youtube,
  discord: Gamepad2,
  tiktok: Video,
  twitter: Twitter,
  facebook: Facebook,
  threads: AtSign,
  bluesky: AtSign,
  linkedin: Linkedin,
  bandcamp: Music,
  soundcloud: Music,
  applemusic: Music,
  venmo: DollarSign,
  cashapp: DollarSign,
  website: Globe,
};

export interface SocialPlatformInfo {
  value: SocialPlatformKey;
  label: string;
}

// Platform display names — ordered by popularity for the music/venue industry
export const SOCIAL_PLATFORMS: SocialPlatformInfo[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'twitter', label: 'X / Twitter' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'threads', label: 'Threads' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'discord', label: 'Discord' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'bandcamp', label: 'Bandcamp' },
  { value: 'soundcloud', label: 'SoundCloud' },
  { value: 'applemusic', label: 'Apple Music' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'cashapp', label: 'Cash App' },
  { value: 'website', label: 'Website' },
];

// Helper function to get platform color
export const getPlatformColor = (platform: SocialPlatformKey | string): string => {
  return PLATFORM_COLORS[platform as SocialPlatformKey] || '#757575';
};

export interface SocialLinkDetail {
  url: string;
  displayText: string;
}

// Array-based social link entry (canonical format for storage and editing)
export interface SocialLinkEntry {
  platform: SocialPlatformKey;
  url: string;
  enabled: boolean;
}

// Normalize socialLinks from either old Record<string, string> format or new SocialLinkEntry[] format
export const normalizeSocialLinks = (raw: any): SocialLinkEntry[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  // Old format: { instagram: "https://...", twitter: "https://..." }
  return Object.entries(raw)
    .filter(([, url]) => typeof url === 'string' && (url as string).trim() !== '')
    .map(([platform, url]) => ({
      platform: platform as SocialPlatformKey,
      url: url as string,
      enabled: true,
    }));
};

// Filter to only visible (enabled + non-empty URL) social links, with each URL
// resolved to something safe to navigate to. A link that can't be resolved is
// dropped rather than rendered — every caller of this puts `.url` in an `href`.
export const getVisibleSocialLinks = (links: SocialLinkEntry[] | Record<string, any> | null | undefined): SocialLinkEntry[] =>
  normalizeSocialLinks(links)
    .filter(l => l.enabled && (l.url || '').trim() !== '')
    .map(l => ({ ...l, url: toExternalUrl(l.url) }))
    .filter((l): l is SocialLinkEntry => l.url !== null);

// Render an icon component for a given platform
export const renderSocialIcon = (
  platform: SocialPlatformKey | string,
  props: any = {}
): React.ReactElement | null => {
  const Icon = SOCIAL_PLATFORM_ICONS[platform as SocialPlatformKey];
  if (!Icon) return <Globe {...props} />;
  return <Icon {...props} />;
};

// Get platform label from key
export const getPlatformLabel = (platform: SocialPlatformKey | string): string => {
  const info = SOCIAL_PLATFORMS.find(p => p.value === platform);
  return info?.label || platform;
};
